import { useEffect, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { Chart, RainOverlay } from "@/components/Chart";
import { ChartType } from "@/stores/ui/Chart";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { observer } from "mobx-react-lite";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { structure } from "@/endpoints/dryp/structure";
import { blockage } from "@/endpoints/dryp/blockage";
import { Spinner } from "@/components/Spinner";
import { DataSourceInfo } from "@/endpoints/rain/rain";
import { SourceInfoBadge } from "./SourceInfo";
import { BlockageDetailsPanel } from "@/pages/Dashboard/Blockages/ExpandedBlockagePanel";
import { type StructureTimePeriodsValue } from "@/config/date";
import { FeatureStatusBadge } from "@/components/FeatureStatusBadge";

interface ParameterGroup {
	title?: string;
	parameters: string[];
	chartType?: ChartType;
}

interface TabConfig {
	id: string;
	label: string;
	parameterNames?: string[];
	parameterGroups?: ParameterGroup[];
	requiresFeature?: "hasIAndI" | "hasBlockage";
}

const IANDI_PARAMETER_GROUPS: ParameterGroup[] = [
	{
		title: "Decomposed Water Flow",
		parameters: ["WasteWaterFlow", "InfiltrationWaterFlow", "RainRunOffFlow"],
		chartType: "area",
	},
	{
		title: "Pipe Flow - Estimated (Level 1)",
		parameters: ["PipeflowEstLvl1"],
		chartType: "area",
	},
	{
		title: "Accumulated Water Volumes",
		parameters: [
			"AccumulatedInfiltrationWaterVolume",
			"AccumulatedRainRunOffVolume",
			"AccumulatedWasteWaterVolume",
		],
	},
	{
		title: "Rain Intensity",
		parameters: ["RainIntensity"],
	},
];

const IANDI_PARAMETERS = [
	"AccumulatedInfiltrationWaterVolume",
	"AccumulatedRainRunOffVolume",
	"AccumulatedWasteWaterVolume",
	"WasteWaterFlow",
	"InfiltrationWaterFlow",
	"RainRunOffFlow",
	"PipeflowEstLvl1",
	"RainIntensity",
];

const BLOCKAGE_PARAMETERS = ["RainIntensity", "PipeFillPercentage"];

const EXCLUDED_FROM_GENERAL = new Set([...IANDI_PARAMETERS, ...BLOCKAGE_PARAMETERS]);

const PREFERRED_GENERAL_PARAMETERS = ["WaterLevel", "WaterFlow"];

const GENERAL_MAX_PARAMETERS = 6;

const TABS: TabConfig[] = [
	{ id: "general", label: "General" },
	{
		id: "infiltration",
		label: "Infiltration & Inflow",
		parameterNames: IANDI_PARAMETERS,
		parameterGroups: IANDI_PARAMETER_GROUPS,
		requiresFeature: "hasIAndI",
	},
	{
		id: "blockages",
		label: "Blockages",
		parameterNames: BLOCKAGE_PARAMETERS,
		requiresFeature: "hasBlockage",
	},
];

const DISABLED_MESSAGES: Record<string, string> = {
	hasIAndI: "Infiltration & Inflow is not configured for this structure",
	hasBlockage: "Blockages is not configured for this structure",
};

type BlockagesTabArgs = {
	blockageApi: {
		loading: boolean;
		error: unknown;
		data: Dryp.Blockage | null;
	};
	from: string;
	to: string;
	timePeriod: StructureTimePeriodsValue;
};

const renderBlockagesTabBody = ({ blockageApi, from, to, timePeriod }: BlockagesTabArgs) => {
	if (blockageApi.loading && !blockageApi.data) {
		return (
			<div className="flex items-center justify-center py-8">
				<Spinner loaderClassName="text-muted-foreground" />
			</div>
		);
	}
	if (blockageApi.error) {
		return <p className="text-muted-foreground text-sm py-8 text-center">Unable to load blockage data</p>;
	}
	if (blockageApi.data) {
		return (
			<BlockageDetailsPanel
				blockage={blockageApi.data}
				from={from}
				to={to}
				timePeriod={timePeriod}
				showSourceBadge={false}
			/>
		);
	}
	return (
		<p className="text-muted-foreground text-sm py-8 text-center">No blockage data available for this structure</p>
	);
};

const renderBlockagesTab = (args: BlockagesTabArgs) => (
	<div className="flex flex-col gap-3">
		<FeatureStatusBadge featureName="Blockages" status="research-preview" />
		{renderBlockagesTabBody(args)}
	</div>
);

type HydraulicPerformanceProps = {
	structureId: Dryp.StructureId;
	hasIAndI: boolean;
	hasBlockage: boolean;
	from: string;
	to: string;
	timePeriod: StructureTimePeriodsValue;
	rainOverlay?: RainOverlay | null;
	sourceInfo?: DataSourceInfo | null;
	structureLat: number;
	structureLon: number;
	structureName: string;
	isExpanded?: boolean;
	onExpandChange?: (expanded: boolean) => void;
};

export const HydraulicPerformance = observer(
	({
		structureId,
		hasIAndI,
		hasBlockage,
		from,
		to,
		rainOverlay,
		sourceInfo,
		structureLat,
		structureLon,
		structureName,
		isExpanded = false,
		onExpandChange,
		timePeriod,
	}: HydraulicPerformanceProps) => {
		const parametersApi = useAsyncStateStore(structure.getStructureParameters);
		const blockageApi = useAsyncStateStore(blockage.getBlockage);
		const [activeTab, setActiveTab] = useState<string>("general");

		useEffect(() => {
			parametersApi.fetch(structureId);
		}, [structureId]);

		useEffect(() => {
			if (
				activeTab === "blockages" &&
				hasBlockage &&
				!blockageApi.data &&
				!blockageApi.loading &&
				!blockageApi.error
			) {
				blockageApi.fetch(structureId);
			}
		}, [activeTab, hasBlockage, structureId]);

		const isNotFound = (parametersApi.error as { status?: number })?.status === 404;
		const hasError = parametersApi.error && !isNotFound;
		const allParameters = parametersApi.data || [];
		const hasNoData = parametersApi.loaded && allParameters.length === 0;

		if (parametersApi.loading) {
			return (
				<div className="rounded-xl bg-card border border-border p-8 flex items-center justify-center min-h-[200px]">
					<Spinner loaderClassName="text-muted-foreground" />
				</div>
			);
		}

		if (isNotFound || hasNoData) {
			return (
				<div className="rounded-xl bg-card border border-border p-8 text-center min-h-[200px] flex items-center justify-center">
					<p className="text-muted-foreground text-sm">No data found for this structure</p>
				</div>
			);
		}

		if (hasError) {
			return (
				<div className="rounded-xl bg-card border border-border p-8 text-center min-h-[200px] flex items-center justify-center">
					<div>
						<p className="text-red-500 text-sm">Failed to load hydraulic performance</p>
						<p className="text-muted-foreground text-xs mt-1">
							{(parametersApi.error as { message?: string })?.message || "An error occurred"}
						</p>
					</div>
				</div>
			);
		}

		const featureFlags = { hasIAndI, hasBlockage };

		const isTabDisabled = (tab: TabConfig): boolean => {
			if (!tab.requiresFeature) return false;
			return !featureFlags[tab.requiresFeature];
		};

		const getDisabledMessage = (tab: TabConfig): string | null => {
			if (!tab.requiresFeature) return null;
			if (featureFlags[tab.requiresFeature]) return null;
			return DISABLED_MESSAGES[tab.requiresFeature];
		};

		const activeTabConfig = TABS.find(tab => tab.id === activeTab);

		const getGeneralParameters = (): Dryp.StructureParameter[] => {
			const availableParams = allParameters.filter(param => !EXCLUDED_FROM_GENERAL.has(param.name));

			const preferredParams = PREFERRED_GENERAL_PARAMETERS.map(name =>
				availableParams.find(param => param.name === name),
			).filter((param): param is Dryp.StructureParameter => param !== undefined);

			const remainingParams = availableParams.filter(param => !PREFERRED_GENERAL_PARAMETERS.includes(param.name));

			const slotsLeft = GENERAL_MAX_PARAMETERS - preferredParams.length;
			const randomParams = remainingParams.slice(0, slotsLeft);

			return [...preferredParams, ...randomParams];
		};

		const parameters =
			activeTab === "general"
				? getGeneralParameters()
				: (activeTabConfig?.parameterNames || [])
						.map(name => allParameters.find(param => param.name === name))
						.filter((param): param is Dryp.StructureParameter => param !== undefined);

		return (
			<TooltipProvider>
				<div className="rounded-xl bg-card border border-border">
					<div className="p-3 lg:p-5 border-b border-border">
						<div className="flex items-center justify-between mb-1">
							<h3 className="text-sm font-medium text-muted-foreground">Hydraulic Performance</h3>
							{onExpandChange && (
								<button
									onClick={() => onExpandChange(!isExpanded)}
									className="inline-flex items-center justify-center h-7 w-7 p-0 rounded-md hover:bg-accent"
								>
									{isExpanded ? (
										<Minimize2 className="w-3.5 h-3.5" />
									) : (
										<Maximize2 className="w-3.5 h-3.5" />
									)}
								</button>
							)}
						</div>
						{sourceInfo && (
							<div className="mb-3">
								<SourceInfoBadge
									sourceInfo={sourceInfo}
									structureLat={structureLat}
									structureLon={structureLon}
									structureName={structureName}
								/>
							</div>
						)}
						<div className="flex gap-1 bg-secondary/50 p-1 rounded-lg">
							{TABS.map(tab => {
								const disabled = isTabDisabled(tab);
								const disabledMessage = getDisabledMessage(tab);

								const tabButton = (
									<button
										key={tab.id}
										onClick={() => !disabled && setActiveTab(tab.id)}
										disabled={disabled}
										className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
											activeTab === tab.id
												? "bg-background text-foreground shadow-sm"
												: disabled
													? "text-muted-foreground/50 cursor-not-allowed"
													: "text-muted-foreground hover:bg-background/50"
										}`}
									>
										{tab.label}
									</button>
								);

								if (disabled && disabledMessage) {
									return (
										<Tooltip key={tab.id}>
											<TooltipTrigger asChild>{tabButton}</TooltipTrigger>
											<TooltipContent>
												<p>{disabledMessage}</p>
											</TooltipContent>
										</Tooltip>
									);
								}

								return tabButton;
							})}
						</div>
					</div>

					<div className="p-3 lg:p-5">
						{activeTab === "blockages" && hasBlockage ? (
							renderBlockagesTab({ blockageApi, from, to, timePeriod })
						) : parameters.length === 0 ? (
							<p className="text-muted-foreground text-sm py-8 text-center">
								No parameters configured for this tab
							</p>
						) : activeTabConfig?.parameterGroups ? (
							<div className="grid gap-3 lg:gap-4 grid-cols-1">
								{activeTabConfig.parameterGroups.map((group, groupIndex) => {
									const groupParams = group.parameters
										.map(name => allParameters.find(p => p.name === name))
										.filter((p): p is Dryp.StructureParameter => p !== undefined);

									if (groupParams.length === 0) return null;

									return (
										<div
											key={group.title || groupIndex}
											className="border-b border-border/50 last:border-b-0 pb-4 last:pb-0"
										>
											<Chart
												structureId={structureId}
												parameters={groupParams}
												from={from}
												to={to}
												title={group.title}
												defaultType={group.chartType}
												showControls={isExpanded}
												className={isExpanded ? "min-h-[250px]" : ""}
												syncId={`hydraulic-${structureId}-${activeTab}`}
												rainOverlay={rainOverlay ?? undefined}
											/>
										</div>
									);
								})}
							</div>
						) : (
							<div
								className={`grid gap-3 lg:gap-4 ${isExpanded ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"}`}
							>
								{parameters.map(param => (
									<div
										key={param.id}
										className="border-b border-border/50 last:border-b-0 pb-4 last:pb-0"
									>
										<Chart
											structureId={structureId}
											parameters={[param]}
											from={from}
											to={to}
											showControls={isExpanded}
											className={isExpanded ? "min-h-[250px]" : ""}
											syncId={`hydraulic-${structureId}-${activeTab}`}
											rainOverlay={rainOverlay ?? undefined}
										/>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</TooltipProvider>
		);
	},
);
